import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { DatabaseService, QuerySignal } from './database.service';
import { LoggerService } from './logger.service';
import { LOCAL_STORAGE } from './user-storage.service';

export interface OrganizationData {
  name: string;
  created_at: string;
  updated_at: string;
}

export interface Organization {
  id: string;
  data: string; // JSON string
}

export interface UserOrganization {
  customer_id: string;
  name: string;
  role: string;
  database_name: string;
}

/**
 * OrganizationService - Manages organization data and tenant context
 *
 * Responsibilities:
 * 1. Current organization (from local customer database)
 * 2. User's organizations list (from HTTP request to backend global database)
 * 3. Organization switching (regenerate JWT with new customer_id)
 *
 * Does NOT:
 * - Manage authentication tokens (AuthService responsibility)
 * - Load user data (UserService responsibility)
 * - Handle sync (CustomSyncService responsibility)
 */
@Injectable({ providedIn: 'root' })
export class OrganizationService {
  private db = inject(DatabaseService);
  private logger = inject(LoggerService).getLogger('organization');
  private localStorage = inject(LOCAL_STORAGE);

  // Current organization state
  currentOrganization = signal<OrganizationData | null>(null);
  currentOrganizationId = signal<string | null>(null);

  // User's organizations list
  userOrganizations = signal<UserOrganization[]>([]);
  private loadedUserId: string | null = null;

  private readonly TOKEN_KEY = 'auth_token';

  /**
   * Create a reactive query for the current organization
   * @param customerId The customer ID (organization ID)
   */
  createOrganizationQuery(customerId: string): QuerySignal<OrganizationData | null> {
    return this.db.createQuery('organization', async () => {
      try {
        const result = await this.db.exec('SELECT data FROM organization WHERE id = ? LIMIT 1', [
          customerId,
        ]);

        if (result && result.length > 0) {
          const orgDataJson = result[0].data as string;
          return JSON.parse(orgDataJson) as OrganizationData;
        }

        return null;
      } catch (error) {
        this.logger.error('Failed to fetch organization:', error);
        return null;
      }
    });
  }

  /**
   * Get organization data directly (non-reactive)
   * @param customerId The customer ID (organization ID)
   */
  async getOrganization(customerId: string): Promise<OrganizationData | null> {
    try {
      const result = await this.db.exec('SELECT data FROM organization WHERE id = ? LIMIT 1', [
        customerId,
      ]);

      if (result && result.length > 0) {
        const orgDataJson = result[0].data as string;
        return JSON.parse(orgDataJson) as OrganizationData;
      }

      return null;
    } catch (error) {
      this.logger.error('Failed to fetch organization:', error);
      return null;
    }
  }

  /**
   * Load current organization from local database
   *
   * @param customerId - Customer ID from JWT token (customer_id claim)
   * @returns Organization data or null if not found
   *
   * Queries the organization table in the customer-specific database
   */
  async loadCurrentOrganization(customerId: string): Promise<OrganizationData | null> {
    this.logger.debug(`loadCurrentOrganization() called - customerId: ${customerId}`);

    try {
      await this.db.init();
      const result = await this.db.exec('SELECT data FROM organization WHERE id = ? LIMIT 1', [
        customerId,
      ]);

      if (result && result.length > 0) {
        const orgDataJson = result[0].data as string;
        const orgData = JSON.parse(orgDataJson) as OrganizationData;
        this.currentOrganization.set(orgData);
        this.currentOrganizationId.set(customerId);
        this.logger.debug(`Organization loaded successfully: ${orgData.name}`);
        return orgData;
      } else {
        this.logger.warn('Organization not found in local database');
        this.currentOrganization.set(null);
        this.currentOrganizationId.set(null);
        return null;
      }
    } catch (error) {
      this.logger.error('Failed to load organization from database:', error);
      this.currentOrganization.set(null);
      this.currentOrganizationId.set(null);
      return null;
    }
  }

  /**
   * Load user's organizations list from backend
   *
   * NOTE: This makes an HTTP request to the backend because user_customers data
   * is stored in the global database (_global.db), not the customer-specific database.
   * Future optimization: Sync user_customers to client-side database.
   *
   * @param userId - User ID from JWT token (sub claim)
   * @param token - Authentication token
   * @returns Array of organizations the user has access to
   *
   * Caching behavior:
   * - Skips HTTP request if already loaded for same userId
   * - Cache invalidated on clearCache() (logout)
   */
  async loadUserOrganizations(userId: string, token: string): Promise<UserOrganization[] | null> {
    this.logger.debug(`loadUserOrganizations() called - userId: ${userId}`);

    // Cache: Skip HTTP request if already loaded for this user
    if (this.loadedUserId === userId && this.userOrganizations().length > 0) {
      this.logger.debug(`Using cached organizations for ${userId}`);
      return this.userOrganizations();
    }

    try {
      const response = await fetch(`${environment.apiUrl}/customers/my-organizations`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        this.logger.error('Failed to fetch user organizations:', response.status, errorText);
        return null;
      }

      const data = await response.json();
      const orgs = (data.organizations || []) as UserOrganization[];

      this.userOrganizations.set(orgs);
      this.loadedUserId = userId;
      this.logger.debug(`Loaded ${orgs.length} organizations for user`);

      return orgs;
    } catch (error) {
      this.logger.error('Error fetching user organizations:', error);
      return null;
    }
  }

  /**
   * Switch to a different organization
   *
   * Updates JWT with new customer_id by calling backend endpoint.
   * This triggers database/sync reset to switch tenant context.
   *
   * @param customerId - The customer_id to switch to
   * @param token - Current authentication token
   * @returns New JWT token if successful, null otherwise
   */
  async switchOrganization(customerId: string, token: string): Promise<string | null> {
    this.logger.debug(`switchOrganization() called - customerId: ${customerId}`);

    try {
      const response = await fetch(`${environment.apiUrl}/customers/switch`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customer_id: customerId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        this.logger.error('Failed to switch organization:', response.status, errorText);
        return null;
      }

      const data = await response.json();
      this.logger.info('Switched to organization:', customerId);

      // Return the new JWT token (contains new customer_id)
      if (data.token) {
        // Store new token in localStorage (consistent with AuthService.storeToken)
        this.localStorage.setItem(this.TOKEN_KEY, data.token);
        return data.token;
      }

      this.logger.error('No token returned from switch organization endpoint');
      return null;
    } catch (error) {
      this.logger.error('Error switching organization:', error);
      return null;
    }
  }

  /**
   * Clear organization cache
   *
   * Called when:
   * - User logs out
   * - User switches organizations (new org will be loaded)
   */
  clearCache(): void {
    this.logger.debug('Clearing organization cache');
    this.currentOrganization.set(null);
    this.currentOrganizationId.set(null);
    this.userOrganizations.set([]);
    this.loadedUserId = null;
  }
}
